import { LegalPageLayout } from "./LegalPageLayout";
import { LegalRouteLink } from "./LegalRouteLink";
import { getSupportEmail, getSupportMailtoHref } from "./supportEmail";
import { privacyUrl } from "@/lib/env";

const SUPPORT_EMAIL = getSupportEmail();
const SUPPORT_MAILTO = getSupportMailtoHref();

export function TermsPage() {
  return (
    <LegalPageLayout title="Terms of Use">
      <h1>Screen0 Terms of Use</h1>
      <p className="legal-muted">Last updated: June 30, 2026</p>

      <h2>1. Acceptance of These Terms</h2>
      <p>
        These Terms of Use (&quot;Terms&quot;) govern your access to and use of the Screen0 mobile
        application and related services (the &quot;Service&quot;). By accessing or using the Service, you
        agree to be bound by these Terms and our{" "}
        <LegalRouteLink href={privacyUrl} className="legal-link">
          Privacy Policy
        </LegalRouteLink>
        . If you do not agree, do not use the Service.
      </p>

      <h2>2. The Service</h2>
      <p>
        Screen0 helps adults understand and control phone usage through personal usage reports, Rules,
        Smart Blocks, habits, and insight features. The Service uses system permissions (Screen Time on
        iOS, Usage Access on Android) to display usage data. The Service is provided for informational
        and self-management purposes and does not replace professional medical, psychological, or legal
        advice.
      </p>

      <h2>3. Eligibility</h2>
      <p>
        The Service is intended for general audiences. If you are under the age required by the laws of
        your country to provide consent to data processing, you must use the Service under the supervision
        and consent of a parent or legal guardian.
      </p>

      <h2>4. Accounts</h2>
      <p>
        Screen0 does not require you to create an account in the MVP. The Service associates data with an
        anonymous device identifier generated on your device. You are responsible for the security of
        your device and for any activity occurring through the Service on that device.
      </p>

      <h2>5. Subscriptions and In-App Purchases</h2>
      <p>
        The Service may offer in-app purchases, which can include an auto-renewable yearly subscription
        (with a free trial) and/or a one-time lifetime non-consumable purchase. Availability, pricing, and
        current offers are shown in the Apple App Store or Google Play Store at the time of purchase.
      </p>
      <p>
        <strong>Yearly subscription</strong>: If you purchase an auto-renewable yearly subscription, it
        may include a free trial period. After the trial, the subscription renews automatically unless
        you cancel it in your App Store / Google Play account settings before the trial or billing period
        ends.
      </p>
      <ul>
        <li>Subscriptions are billed through your Apple ID or Google Play account after purchase confirmation.</li>
        <li>
          Subscriptions automatically renew unless cancelled at least 24 hours before the end of the
          current period.
        </li>
        <li>
          You can manage and cancel subscriptions in your Apple App Store or Google Play account
          settings.
        </li>
      </ul>
      <p>
        <strong>Lifetime purchase</strong>: If you purchase a lifetime non-consumable, you receive
        ongoing access to the premium features described at the time of purchase. Lifetime purchases are
        not subscriptions and do not renew automatically.
      </p>
      <ul>
        <li>Lifetime purchases are generally available once per Apple ID or Google account.</li>
        <li>Lifetime access has no cash value and is not transferable.</li>
        <li>Refunds are handled by Apple or Google in accordance with their refund policies.</li>
      </ul>
      <p>
        Purchase verification may be mediated by third-party subscription infrastructure (such as
        RevenueCat). We receive only the information needed to verify your entitlement.
      </p>

      <h2>6. User Content</h2>
      <p>
        The Service processes usage data generated by your device and apps you use, solely to provide
        reports and related features. You retain control over system permissions that enable this
        processing. We do not claim ownership of your device usage data beyond the limited license needed
        to operate the Service.
      </p>

      <h2>7. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for unlawful, harmful, or fraudulent activities.</li>
        <li>Interfere with or disrupt the Service or its security.</li>
        <li>Copy, modify, reverse-engineer, or attempt to derive source code from the Service.</li>
        <li>Circumvent usage limits, entitlements, or technical protections.</li>
      </ul>

      <h2>8. External Services</h2>
      <p>
        The Service may include links to third-party services or websites (including Apple, Google, and
        legal or support pages). We do not control and are not responsible for those services. Your use of
        third-party services is at your own risk and subject to their terms.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        The Service and its content, features, and branding are owned by Ferruccio Nati or licensed to us
        and are protected by intellectual property laws. These Terms do not grant you any rights to use
        our trademarks or other brand features.
      </p>

      <h2>10. Disclaimer of Warranties</h2>
      <p>
        The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any
        kind, whether express, implied, or statutory, including implied warranties of merchantability,
        fitness for a particular purpose, and non-infringement. Usage statistics depend on system APIs and
        permissions; accuracy may vary by platform. Your use of the Service is at your sole risk.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Ferruccio Nati will not be liable for any indirect,
        incidental, special, consequential, or punitive damages, or any loss of profits or data, arising
        out of or related to your use of the Service. To the extent liability is not excluded, total
        liability will be limited to the amount you paid for the Service in the twelve months preceding
        the event giving rise to the claim.
      </p>

      <h2>12. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service if you violate these Terms or if required
        to do so by law. You may stop using the Service at any time by uninstalling the App and cancelling
        any active subscriptions through your store account.
      </p>

      <h2>13. Apple App Store Terms (iOS)</h2>
      <p>
        If you download the iOS version of the Service, Apple&apos;s Standard End User License Agreement
        (EULA) applies. You can review it here:{" "}
        <a
          href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
          className="legal-link"
          target="_blank"
          rel="noreferrer"
        >
          Apple Standard EULA
        </a>
        .
      </p>

      <h2>14. Governing Law</h2>
      <p>
        These Terms are governed by the laws of Italy, without regard to its conflict of laws rules. If
        you are a consumer, mandatory consumer protection laws in your country of residence may also
        apply.
      </p>

      <h2>15. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. We will update the &quot;Last updated&quot; date and,
        where required, provide additional notice. Your continued use of the Service after changes become
        effective means you accept the updated Terms.
      </p>

      <h2>16. Contact</h2>
      <p>
        If you have questions about these Terms, contact us at{" "}
        <a href={SUPPORT_MAILTO} className="legal-link">
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </LegalPageLayout>
  );
}
